import { css } from '@emotion/react'
import { ReactNode, memo, useCallback, useState } from 'react'
import { AppRouter, routeConfigs } from '../../state/state.router'
import { routerPush } from '../../utilities/router'
import { palette } from '../styles/colors'

const _block = css({
    color: 'inherit',
    display: 'block',
})

const _expand = css({
    alignItems: 'center',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    textDecoration: 'none',
})

const _inline = css({
    color: '#777',
    cursor: 'pointer',
    textDecoration: 'none',
})

const _logo = css({
    alignItems: 'center',
    display: 'flex',
    textDecoration: 'none',
})

const _mfc = css({
    color: `${palette.greenLink} !important`,
    cursor: 'pointer',
    fontWeight: 'bold',
})

const _thin = css({
    fontWeight: 500,
})

const _nav = css({
    display: 'flex',
    alignItems: 'center',
    color: '#fff',
    cursor: 'pointer',
    textDecoration: 'none',
})

const _text = css({
    cursor: 'pointer',
    textDecoration: 'none',
})

const _underline = css({
    textDecoration: 'underline',
})

const getRootStyles = (props: ILink) => {
    const {
        actionLink,
        block,
        href,
        inline,
        logo,
        thin,
        mfc,
        nav,
        text,
        underline,
    } = props

    if (nav) return _nav
    if (actionLink)
        return [_inline, mfc && _mfc, thin && _thin, underline && _underline]
    if (text && mfc) return [_mfc, thin && _thin]
    if (!inline && !logo) return _expand
    if (href?.startsWith('http') && !inline && !logo && !block) {
        return _expand
    }
    const defaultStyles = []
    if (block) defaultStyles.push(_block)
    if (inline) defaultStyles.push(_inline)
    if (logo) defaultStyles.push(_logo)
    if (mfc) defaultStyles.push(_mfc)
    if (text) defaultStyles.push(_text)
    if (underline) defaultStyles.push(_underline)
    if (thin) defaultStyles.push(_thin)

    return defaultStyles
}

interface ILink {
    className?: string
    disabled?: boolean
    onClick?(event?: any): void
    actionLink?: boolean
    underline?: boolean
    children: ReactNode
    inline?: boolean
    block?: boolean
    text?: boolean
    href?: string
    logo?: boolean
    thin?: boolean
    mfc?: boolean
    nav?: boolean
    title?: string
    page?:
        | 'rooms'
        | 'messages'
        | 'chat-archives'
        | 'recommendations'
        | 'home'
        | 'search'
        | 'friends'
        | 'private-gallery'
        | 'notes'
        | 'mail'
        | 'news'
}

const Link = memo((props: ILink) => {
    const {
        className,
        disabled,
        onClick = () => {},
        actionLink,
        children,
        text,
        href,
        mfc,
        title,
        page,
    } = props
    const rootStyles = getRootStyles(props)
    const [preloaded, setPreloaded] = useState(false)
    const onNavIntention = useCallback(async () => {
        // don't preload for the route that you are already on
        if (page && AppRouter.route.visiblePage !== page) {
            const foundRoute = routeConfigs.get(page)
            if (foundRoute.preload) {
                if (foundRoute.skipAwait) {
                    if (foundRoute.floater)
                        AppRouter.route.preloadFloatingData =
                            foundRoute.preload()
                    else AppRouter.route.preloadBaseData = foundRoute.preload()
                } else {
                    if (foundRoute.floater)
                        AppRouter.route.preloadFloatingData =
                            await foundRoute.preload()
                    else
                        AppRouter.route.preloadBaseData =
                            await foundRoute.preload()
                }
                setPreloaded(true)
            }
        }
    }, [page])
    if ((text && mfc) || actionLink) {
        // only used for the Empty components at the moment.  also referenced in the dead You May Like search results
        return (
            <span
                css={rootStyles}
                className={className}
                title={title}
                onClick={disabled ? null : onClick}
                onMouseEnter={onNavIntention}
            >
                {children}
            </span>
        )
    }

    if (href && href.startsWith('http')) {
        return (
            <a
                className={className}
                css={rootStyles}
                rel="noopener noreferrer"
                target="_blank"
                href={href}
                title={title}
            >
                {children}
            </a>
        )
    }
    return (
        <a
            title={title}
            onMouseEnter={onNavIntention}
            onClick={
                disabled
                    ? null
                    : () => {
                          const resolvedHref =
                              '/' +
                              (page ? routeConfigs.get(page).paths[0] : href)
                          routerPush(resolvedHref, { skipPreload: preloaded })
                      }
            }
            className={className}
            css={rootStyles}
        >
            {children}
        </a>
    )
})

export default Link
